import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Clock, DollarSign, Package, User, Building2, 
  FileText, Send, Sparkles, CheckCircle, AlertCircle,
  Save, Loader2, ChevronDown, ChevronUp, Download, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { useTranslation } from '../../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AFAWorkspaceView = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { language } = useTranslation();
  
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDelivering, setIsDelivering] = useState(false);
  const [showClientDetails, setShowClientDetails] = useState(true);
  const [showDocuments, setShowDocuments] = useState(true);
  const [isGeneratingDraft, setIsGeneratingDraft] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  
  useEffect(() => {
    fetchCaseData();
  }, [caseId]);
  
  const fetchCaseData = async () => {
    try {
      setLoading(true);
      const token = auth.getToken();
      const response = await axios.get(`${API}/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const caseInfo = response.data;
      setCaseData(caseInfo);
      
      if (caseInfo.lawyer_response) {
        setAnswer(caseInfo.lawyer_response);
      } else if (caseInfo.afa_ai_draft) {
        setAnswer(caseInfo.afa_ai_draft);
      }
    } catch (error) {
      console.error('Error fetching case:', error);
      toast.error(language === 'en' ? 'Could not load case' : 'Kunne ikke laste saken');
      navigate('/lexy/dashboard?tab=cases');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveDraft = async () => {
    try {
      setIsSaving(true);
      const token = auth.getToken();
      
      await axios.patch(
        `${API}/cases/${caseId}`,
        { lawyer_response: answer },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(language === 'en' ? 'Draft saved!' : 'Utkast lagret!');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error(language === 'en' ? 'Could not save draft' : 'Kunne ikke lagre utkast');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleDeliverAnswer = async () => {
    if (!answer.trim()) {
      toast.error(language === 'en' ? 'You must write an answer before delivering' : 'Du må skrive et svar før du kan levere');
      return;
    }
    
    try {
      setIsDelivering(true);
      const token = auth.getToken();
      
      await axios.patch(
        `${API}/cases/${caseId}`,
        { 
          lawyer_response: answer,
          afa_status: 'DELIVERED',
          status: 'COMPLETED',
          delivered_at: new Date().toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(language === 'en' ? '🎉 Answer delivered to client!' : '🎉 Svar levert til klient!');
      
      setTimeout(() => {
        navigate('/lexy/dashboard?tab=cases');
      }, 2000);
    } catch (error) {
      console.error('Error delivering answer:', error);
      toast.error(language === 'en' ? 'Could not deliver answer' : 'Kunne ikke levere svar');
    } finally {
      setIsDelivering(false);
    }
  };
  
  const handleGenerateAIDraft = async () => {
    try {
      setIsGeneratingDraft(true);
      const token = auth.getToken();
      
      toast.info(language === 'en' ? '🤖 Generating AI draft...' : '🤖 Genererer AI-utkast...');
      
      const response = await axios.post(
        `${API}/afa/marketplace/generate-draft/${caseId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAnswer(response.data.draft);
      toast.success(language === 'en' 
        ? `✨ AI draft generated! (${response.data.word_count} words)` 
        : `✨ AI-utkast generert! (${response.data.word_count} ord)`);
    } catch (error) {
      console.error('Error generating AI draft:', error);
      if (error.response?.status === 403) {
        toast.error(language === 'en' ? 'No access to generate draft for this case' : 'Du har ikke tilgang til å generere utkast for denne saken');
      } else {
        toast.error(language === 'en' ? 'Could not generate AI draft. Try again.' : 'Kunne ikke generere AI-utkast. Prøv igjen.');
      }
    } finally {
      setIsGeneratingDraft(false);
    }
  };
  
  const handleUpgradeToAdvanced = async () => {
    try {
      setIsUpgrading(true);
      const token = auth.getToken();
      
      await axios.post(
        `${API}/afa/marketplace/upgrade-to-advanced/${caseId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(language === 'en' ? '🚀 Case upgraded to Advanced mode!' : '🚀 Sak oppgradert til Avansert modus!');
      
      setTimeout(() => {
        navigate(`/lexy/case/${caseId}/workspace`);
      }, 1000);
    } catch (error) {
      console.error('Error upgrading case:', error);
      if (error.response?.status === 403) {
        toast.error(language === 'en' ? 'No access to upgrade this case' : 'Du har ikke tilgang til å oppgradere denne saken');
      } else if (error.response?.status === 400) {
        toast.error(language === 'en' ? 'Case is already advanced' : 'Saken er allerede en avansert sak');
      } else {
        toast.error(language === 'en' ? 'Could not upgrade case' : 'Kunne ikke oppgradere saken');
      }
    } finally {
      setIsUpgrading(false);
      setShowUpgradeModal(false);
    }
  };
  
  const getTimeRemaining = (deadline) => {
    if (!deadline) return null;
    
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffMs = deadlineDate.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMs < 0) return { text: language === 'en' ? 'Expired' : 'Utløpt', urgent: true };
    if (diffHours < 24) return { text: `${diffHours} ${language === 'en' ? 'hours left' : 'timer igjen'}`, urgent: true };
    return { text: `${diffDays} ${language === 'en' ? 'days left' : 'dager igjen'}`, urgent: false };
  };
  
  const getPackageLabel = (packageLevel) => {
    const labels = {
      'BASIS': 'Basis',
      'DOKUMENT': language === 'en' ? 'Document' : 'Dokument',
      'UTVIDET': language === 'en' ? 'Extended' : 'Utvidet'
    };
    return labels[packageLevel] || 'Basis';
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">{language === 'en' ? 'Loading AFA case...' : 'Laster AFA-sak...'}</p>
        </div>
      </div>
    );
  }
  
  if (!caseData) {
    return null;
  }
  
  const timeRemaining = getTimeRemaining(caseData.afa_deadline);
  
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-8 py-10">
        
        {/* Header */}
        <header className="mb-10">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate('/lexy/dashboard?tab=cases')}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              {language === 'en' ? 'Back to cases' : 'Tilbake til saker'}
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
                {language === 'en' ? 'AFA Workspace' : 'AFA Arbeidsområde'}
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                {language === 'en' ? 'Simplified view for fast case processing' : 'Forenklet visning for rask saksbehandling'}
              </p>
            </div>
            <button
              onClick={handleGenerateAIDraft}
              disabled={isGeneratingDraft}
              className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              {isGeneratingDraft ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {language === 'en' ? 'Generating...' : 'Genererer...'}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  {language === 'en' ? 'AI Draft' : 'AI-utkast'}
                </>
              )}
            </button>
          </div>
        </header>

        {/* Stats Row */}
        <div className="flex items-center gap-10 mb-10 pb-8 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-500">{language === 'en' ? 'Package' : 'Pakke'}</span>
            </div>
            <div className="text-2xl font-semibold text-gray-900 mt-1">
              AFA {getPackageLabel(caseData.afa_package_level)}
            </div>
          </div>
          <div className="w-px h-12 bg-gray-200" />
          <div>
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-500">{language === 'en' ? 'Price' : 'Pris'}</span>
            </div>
            <div className="text-2xl font-semibold text-gray-900 mt-1">
              {caseData.afa_package_price?.toLocaleString('nb-NO') || '0'} kr
            </div>
          </div>
          {timeRemaining && (
            <>
              <div className="w-px h-12 bg-gray-200" />
              <div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-500">{language === 'en' ? 'Deadline' : 'Frist'}</span>
                </div>
                <div className={`text-2xl font-semibold mt-1 ${timeRemaining.urgent ? 'text-orange-600' : 'text-gray-900'}`}>
                  {timeRemaining.text}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          
          {/* Left Column - Case Details */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* Client Info */}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setShowClientDetails(!showClientDetails)}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {caseData.client_type === 'COMPANY' ? (
                    <Building2 className="w-5 h-5 text-gray-400" />
                  ) : (
                    <User className="w-5 h-5 text-gray-400" />
                  )}
                  <h3 className="font-medium text-gray-900">
                    {language === 'en' ? 'Client Information' : 'Klientinformasjon'}
                  </h3>
                </div>
                {showClientDetails ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>
              
              {showClientDetails && (
                <div className="px-5 pb-5 space-y-3 text-sm border-t border-gray-100">
                  <div className="pt-4">
                    <p className="text-xs text-gray-500 mb-1">{language === 'en' ? 'Type' : 'Type'}</p>
                    <p className="font-medium text-gray-900">
                      {caseData.client_type === 'COMPANY' 
                        ? (language === 'en' ? 'Company' : 'Bedrift') 
                        : (language === 'en' ? 'Individual' : 'Privatperson')}
                    </p>
                  </div>
                  {caseData.client_id && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{language === 'en' ? 'Client ID' : 'Klient ID'}</p>
                      <p className="font-mono text-xs text-gray-700">{caseData.client_id}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Case Details */}
            <div className="border border-gray-200 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="w-5 h-5 text-gray-400" />
                <h3 className="font-medium text-gray-900">
                  {language === 'en' ? 'Case Details' : 'Saksdetaljer'}
                </h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">{language === 'en' ? 'Topic' : 'Emne'}</p>
                  <p className="font-medium text-gray-900">{caseData.topic}</p>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500 mb-1">{language === 'en' ? 'Description' : 'Beskrivelse'}</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{caseData.intake_summary}</p>
                </div>
                
                {caseData.case_type && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">{language === 'en' ? 'Case Type' : 'Sakstype'}</p>
                    <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                      {caseData.case_type}
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Documents */}
            {caseData.uploaded_documents && caseData.uploaded_documents.length > 0 && (
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setShowDocuments(!showDocuments)}
                  className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-gray-400" />
                    <h3 className="font-medium text-gray-900">
                      {language === 'en' ? 'Documents' : 'Dokumenter'} ({caseData.uploaded_documents.length})
                    </h3>
                  </div>
                  {showDocuments ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                
                {showDocuments && (
                  <div className="px-5 pb-5 space-y-2 border-t border-gray-100 pt-4">
                    {caseData.uploaded_documents.map((doc, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="text-sm flex-1 truncate text-gray-700">
                          {doc.name || `${language === 'en' ? 'Document' : 'Dokument'} ${index + 1}`}
                        </span>
                        <button type="button" className="text-gray-400 hover:text-gray-600">
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Upgrade to Advanced */}
            <div className="p-5 bg-gray-50 rounded-xl">
              <h4 className="font-medium text-gray-900 mb-2">
                {language === 'en' ? 'Need more comprehensive assistance?' : 'Trenger klienten mer omfattende bistand?'}
              </h4>
              <p className="text-sm text-gray-500 mb-4">
                {language === 'en' 
                  ? 'Upgrade to Advanced mode with full workspace, document management, and Lexy AI assistant.'
                  : 'Oppgrader til Avansert modus med full workspace, dokumenthåndtering, og Lexy AI-assistent.'}
              </p>
              <button
                onClick={() => setShowUpgradeModal(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <TrendingUp className="w-4 h-4" />
                {language === 'en' ? 'Upgrade to Advanced' : 'Oppgrader til Avansert'}
              </button>
            </div>
          </div>
          
          {/* Right Column - Answer Area */}
          <div className="lg:col-span-2">
            <div className="border border-gray-200 rounded-xl p-6 space-y-6">
              {/* Header */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {language === 'en' ? 'Your answer to client' : 'Ditt svar til klient'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {language === 'en' ? 'Write or edit the answer to be sent to the client' : 'Skriv eller rediger svaret som sendes til klienten'}
                </p>
              </div>
              
              {/* Answer Textarea */}
              <div>
                <textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder={language === 'en' ? 'Write your legal advice or answer here...' : 'Skriv ditt juridiske råd eller svar her...'}
                  rows={16}
                  className="w-full px-4 py-3 bg-gray-50 border-0 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-slate-900/10 text-gray-900 placeholder-gray-400 transition-all"
                />
                <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                  <span>{answer.length} {language === 'en' ? 'characters' : 'tegn'}</span>
                  <span>{Math.ceil(answer.split(' ').filter(w => w).length / 200)} min {language === 'en' ? 'reading' : 'lesing'}</span>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveDraft}
                  disabled={isSaving || !answer.trim()}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {language === 'en' ? 'Saving...' : 'Lagrer...'}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {language === 'en' ? 'Save Draft' : 'Lagre utkast'}
                    </>
                  )}
                </button>
                
                <button
                  onClick={handleDeliverAnswer}
                  disabled={isDelivering || !answer.trim()}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDelivering ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {language === 'en' ? 'Delivering...' : 'Leverer...'}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {language === 'en' ? 'Deliver to client' : 'Lever svar til klient'}
                    </>
                  )}
                </button>
              </div>
              
              {/* Info Banner */}
              <div className="p-4 bg-blue-50 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-900 mb-1">
                    {language === 'en' ? 'Important information' : 'Viktig informasjon'}
                  </p>
                  <p className="text-blue-700">
                    {language === 'en' 
                      ? 'When you deliver the answer, it will be sent directly to the client and the case will be marked as completed. Make sure the answer is complete and quality assured before delivery.'
                      : 'Når du leverer svaret, vil det sendes direkte til klienten og saken markeres som fullført. Sørg for at svaret er komplett og kvalitetssikret før levering.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Upgrade Confirmation Modal */}
      {showUpgradeModal && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => !isUpgrading && setShowUpgradeModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white max-w-lg w-full rounded-xl shadow-2xl p-6 space-y-6"
          >
            {/* Icon & Title */}
            <div className="text-center">
              <div className="w-14 h-14 bg-slate-900 rounded-xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {language === 'en' ? 'Upgrade to Advanced Mode?' : 'Oppgrader til Avansert Modus?'}
              </h3>
              <p className="text-sm text-gray-500">
                {language === 'en' 
                  ? 'This will convert the AFA case to an advanced case with full functionality'
                  : 'Dette vil konvertere AFA-saken til en avansert sak med full funksjonalitet'}
              </p>
            </div>
            
            {/* Benefits List */}
            <div className="space-y-3 p-4 bg-gray-50 rounded-xl">
              <p className="font-medium text-sm text-gray-900 mb-3">
                {language === 'en' ? 'What you get with Advanced mode:' : 'Hva du får med Avansert modus:'}
              </p>
              {[
                language === 'en' ? 'Full Lexy AI assistant for continuous support' : 'Full Lexy AI-assistent for kontinuerlig støtte',
                language === 'en' ? 'Advanced document management and organization' : 'Avansert dokumenthåndtering og organisering',
                language === 'en' ? 'Collaborate with team members' : 'Samarbeid med teammedlemmer',
                language === 'en' ? 'Timeline, deadlines and milestones' : 'Tidslinje, frister og milepæler',
                language === 'en' ? 'Internal notes and comments' : 'Interne notater og kommentarer'
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="text-sm text-gray-700">{item}</span>
                </div>
              ))}
            </div>
            
            {/* Warning */}
            <div className="p-3 bg-amber-50 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                <span className="font-semibold">{language === 'en' ? 'Note:' : 'OBS:'}</span>{' '}
                {language === 'en' 
                  ? 'This action cannot be undone. AFA data is retained for history.'
                  : 'Denne handlingen kan ikke angres. AFA-dataene beholdes for historikk.'}
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowUpgradeModal(false)}
                disabled={isUpgrading}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                {language === 'en' ? 'Cancel' : 'Avbryt'}
              </button>
              
              <button
                onClick={handleUpgradeToAdvanced}
                disabled={isUpgrading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                {isUpgrading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {language === 'en' ? 'Upgrading...' : 'Oppgraderer...'}
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    {language === 'en' ? 'Yes, upgrade now' : 'Ja, oppgrader nå'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AFAWorkspaceView;
